import React, { Key } from 'react';
import { ColumnType } from 'antd/es/table';
import { EUpdateStatus } from 'common/models/commonModel';
import { priceFormatter } from 'common/helpers/format.helper';
import { dateFormatter } from 'common/helpers/date.helper';
import { PurchaseOrderStatus } from 'entities/PurchaseOrder/components/PurchaseOrderStatus';

// Local extension until `yarn models:build` picks up the new BE filters (MBC-51-fix pattern).
export interface IPurchaseOrderCollectionParams extends Paths.PurchaseOrderControllerList.QueryParameters {
  quarter?: number;
  month?: number;
  date?: string;
}
export interface IPurchaseOrderCollection extends Components.Schemas.PurchaseOrderCollectionDto {}
export interface IPurchaseOrder extends Components.Schemas.PurchaseOrderDto {}

export interface IPurchaseOrderModelParams extends Paths.PurchaseOrderControllerGetBySonum.PathParameters {}
export interface IPurchaseOrderModel extends Components.Schemas.PurchaseOrderDetailedDto {}
export interface IPurchaseOrderCreatePayload extends Components.Schemas.CreatePurchaseOrderDto {}
export interface IPurchaseOrderItem extends Components.Schemas.PurchaseOrderItemDto {
  key: Key;
}

export interface IPurchaseOrderStoreCollection extends Components.Schemas.PurchaseOrderStoreCollectionDto {}
export interface IPurchaseOrderStoreModel extends Components.Schemas.PurchaseOrderStoreDto {
  id?: string | number;
}

// MBC-85: Track PO RECO column items.
export interface IPurchaseOrderRecoStoreModel extends Components.Schemas.PurchaseOrderRecoStoreDto {}
export interface IPurchaseOrderRecoStoreCollection extends Components.Schemas.PurchaseOrderRecoStoreCollectionDto {}

export interface IPurchaseOrderRecoParams extends Paths.PurchaseOrderControllerGetReco.QueryParameters {}
// Local override until typegen marks cutoffReason nullable: null means the store can order now.
export interface IPurchaseOrderRecoModel extends Omit<Components.Schemas.PurchaseOrderRecoDto, 'cutoffReason'> {
  cutoffReason: ECutoffReason | null;
}
export interface IPurchaseOrderProduct extends Components.Schemas.PurchaseOrderProductDto {}
export interface IPurchaseOrderRecoProduct extends IPurchaseOrderProduct {
  key: Key;
  orderCount: number;
  status?: EUpdateStatus;
}

export interface IPurchaseOrderOutstandingPayablesModel
  extends Components.Schemas.PurchaseOrderOutstandingPayablesDto {}

export interface IPurchaseOrderRecoBasisFilters extends Paths.PurchaseOrderControllerGetRecoBasis.QueryParameters {}
// Local override until `yarn models:build` regenerates the typegen: rewritten-formula fields
// (preForecast / orderToDeliveryDemand / finalOrderQty) replace initialReco / adjInitialReco.
export interface IPurchaseOrderRecoBasisModel
  extends Omit<Components.Schemas.PurchaseOrderRecoBasisDto, 'initialReco' | 'adjInitialReco'> {
  preForecast: number;
  orderToDeliveryDemand: number;
  finalOrderQty: number;
}

export interface IRecoBasisUnitsSoldDataSourceItem {
  key: string;
  criteria: string;
  sun: number;
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
}

export interface IRecoBasisTrendDataSourceItem {
  key: string;
  last4Wk: number;
  last8Wk: number;
  trendFactor: number;
}

export interface IRecoBasisSellingPeriodDataSourceItem {
  key: string;
  sellingPeriod: string;
  periodSum: number;
  currentInventory: number;
  preForecast: number;
}

export interface IRecoBasisOrderToDeliveryDataSourceItem {
  key: string;
  orderToDeliveryDemand: number;
}

export interface IRecoBasisSafetyStockDataSourceItem {
  key: string;
  safetyDay: string;
  safetyStock: number;
  finalOrderQty: number;
  productSet: number;
  reco: number;
}

export interface IRecoBasisDataSources {
  unitsSoldDataSource: IRecoBasisUnitsSoldDataSourceItem[];
  trendDataSource: IRecoBasisTrendDataSourceItem[];
  adjDemandDataSource: IRecoBasisUnitsSoldDataSourceItem[];
  sellingPeriodDataSource: IRecoBasisSellingPeriodDataSourceItem[];
  orderToDeliveryDataSource: IRecoBasisOrderToDeliveryDataSourceItem[];
  safetyStockDataSource: IRecoBasisSafetyStockDataSourceItem[];
}

export enum EPurchaseOrderStatus {
  ForApproval = 'forApproval',
  Preparing = 'preparing',
  InTransit = 'inTransit',
  Delivered = 'delivered',
  Canceled = 'canceled',
}

export enum EPurchaseOrderStatusLabel {
  forApproval = 'For Approval',
  preparing = 'Preparing Order',
  inTransit = 'In Transit',
  delivered = 'Delivered',
  canceled = 'Canceled',
}

// Why ordering is closed for the store right now (backend-computed from the store schedule).
export enum ECutoffReason {
  WindowClosed = 'WINDOW_CLOSED',
  NotYourDeliveryDay = 'NOT_YOUR_DELIVERY_DAY',
}

export enum EPurchaseOrderStatusFilterKey {
  // MBC-85: Track PO RECO column — stores without active SO on next delivery date.
  Reco = 'reco',
  ForApproval = 'forApproval',
  Preparing = 'preparing',
  InTransit = 'inTransit',
  DeliveredCanceled = 'delivered/canceled',
}

export enum EPurchaseOrderStatusFilterLabel {
  reco = 'RECO',
  forApproval = 'For Approval',
  preparing = 'Preparing Order',
  inTransit = 'In Transit',
  'delivered/canceled' = 'Delivered/Canceled',
}

const alignEnd = {
  onHeaderCell: () => ({ style: { textAlign: 'end' as const } }),
  onCell: () => ({ style: { textAlign: 'end' as const } }),
};

export const getPurchaseOrderItemsConfig = (status?: string): ColumnType<IPurchaseOrderItem>[] => {
  // Bugs Track PO #7: delivered order = DR view (SO/SI qty + variance, adjusted cost);
  // any other status = sales order view (plain Quantity / Total Cost, no SI columns).
  const isDelivered = status === EPurchaseOrderStatus.Delivered;

  return [
    {
      key: 'name',
      dataIndex: 'name',
      title: 'Item',
    },
    {
      key: 'orderCount',
      dataIndex: 'orderCount',
      title: isDelivered ? 'SO Qty' : 'Quantity',
      ...alignEnd,
    },
    ...(isDelivered
      ? ([
          {
            key: 'siQty',
            dataIndex: 'siQty',
            title: 'SI Qty',
            ...alignEnd,
          },
          {
            key: 'variance',
            dataIndex: 'variance',
            title: 'Var',
            ...alignEnd,
          },
        ] as ColumnType<IPurchaseOrderItem>[])
      : []),
    {
      key: 'price',
      dataIndex: 'price',
      title: 'Unit Cost',
      ...alignEnd,
      render: (_, record) => priceFormatter(record.price),
    },
    {
      key: 'total',
      title: isDelivered ? 'Adjusted Total Cost' : 'Total Cost',
      ...alignEnd,
      // BE-computed adjustedTotalCost: SO statuses — SO_QTY × price, delivered (DR) — SI_QTY × price.
      render: (_, record) => priceFormatter(record.adjustedTotalCost),
    },
  ];
};

export const deliveryHistoryConfig: ColumnType<IPurchaseOrder>[] = [
  {
    key: 'storeCode',
    dataIndex: 'storeCode',
    title: 'Store Code',
    sorter: true,
  },
  {
    key: 'sonum',
    dataIndex: 'sonum',
    title: 'SO#',
    sorter: true,
  },
  {
    key: 'orderedAt',
    dataIndex: 'orderedAt',
    title: 'Ordered',
    sorter: true,
    render: (text) => dateFormatter(text, 'MM/DD/YYYY HH:mm'),
  },
  {
    key: 'amount',
    dataIndex: 'amount',
    title: 'Order Amount',
    sorter: true,
    render: (text) => priceFormatter(text),
    onHeaderCell: () => {
      return {
        style: { display: 'flex', justifyContent: 'end' },
        className: '',
      };
    },
    onCell: () => {
      return {
        style: { textAlign: 'end' },
      };
    },
  },
  {
    key: 'deliveredAt',
    dataIndex: 'deliveredAt',
    title: 'Delivered',
    sorter: true,
    render: (text) => dateFormatter(text, 'MM/DD/YYYY HH:mm'),
  },
  {
    key: 'status',
    dataIndex: 'status',
    title: 'Order Status',
    sorter: true,
    render: (text) => <PurchaseOrderStatus status={text} />,
  },
];

export const recoBasisUnitsSoldDataConfig = [
  {
    key: 'criteria',
    title: 'Criteria',
    dataIndex: 'criteria',
    width: 122,
  },
  {
    key: 'sun',
    title: 'S',
    dataIndex: 'sun',
  },
  {
    key: 'mon',
    title: 'M',
    dataIndex: 'mon',
  },
  {
    key: 'tue',
    title: 'T',
    dataIndex: 'tue',
  },
  {
    key: 'wed',
    title: 'W',
    dataIndex: 'wed',
  },
  {
    key: 'thu',
    title: 'T',
    dataIndex: 'thu',
  },
  {
    key: 'fri',
    title: 'F',
    dataIndex: 'fri',
  },
  {
    key: 'sat',
    title: 'S',
    dataIndex: 'sat',
  },
];

export const recoBasisTrendConfig = [
  {
    key: 'last4Wk',
    title: 'Last 4 wk',
    dataIndex: 'last4Wk',
    width: 122,
  },
  {
    key: 'last8Wk',
    title: 'Last 8 wk',
    dataIndex: 'last8Wk',
    width: 122,
  },
  {
    key: 'trendFactor',
    title: 'Trend Factor',
    dataIndex: 'trendFactor',
  },
];

export const recoBasisSellingPeriodConfig = [
  {
    key: 'sellingPeriod',
    title: 'Selling Period',
    dataIndex: 'sellingPeriod',
    width: 122,
  },
  {
    key: 'periodSum',
    title: 'Period Sum',
    dataIndex: 'periodSum',
    width: 122,
  },
  {
    key: 'currentInventory',
    title: 'Current Inv.',
    dataIndex: 'currentInventory',
    width: 122,
  },
  {
    key: 'preForecast',
    title: 'Pre-Forecast',
    dataIndex: 'preForecast',
  },
];

export const recoBasisOrderToDeliveryConfig = [
  {
    key: 'orderToDeliveryDemand',
    title: 'Order-to-Delivery Demand',
    dataIndex: 'orderToDeliveryDemand',
  },
];

export const recoBasisSafetyStockConfig = [
  {
    key: 'safetyDay',
    title: '1/3 of',
    dataIndex: 'safetyDay',
    width: 122,
  },
  {
    key: 'safetyStock',
    title: 'Safety Stock',
    dataIndex: 'safetyStock',
    width: 122,
  },
  {
    key: 'finalOrderQty',
    title: 'Final Order QTY',
    dataIndex: 'finalOrderQty',
    width: 122,
  },
  {
    key: 'productSet',
    title: 'Product Set Order QTY',
    dataIndex: 'productSet',
    width: 122,
  },
  {
    key: 'reco',
    title: 'Reco',
    dataIndex: 'reco',
  },
];

export const VIEW_DELIVERY_HISTORY_SORT_FIELD: Record<string, string> = {
  storeCode: 'storeCode',
  sonum: 'sonum',
  orderedAt: 'createdAt',
  amount: 'amount',
  deliveredAt: 'updatedAt',
  status: 'status',
};

export const VIEW_DELIVERY_HISTORY_LIMIT = 50;
